/**
 * Staff in-app notification center (tracker item 62). Mirrors backend
 * modules/staff-notifications/staff-notifications.types.ts.
 */

export type StaffNotificationType =
  | 'SERVICE_REQUEST_RECEIVED'
  | 'RESCHEDULE_REQUESTED'
  | 'CUSTOMER_REPLY'
  | 'AUTOPAY_FAILED'
  | 'PAYMENT_RECEIVED'
  | 'ESTIMATE_APPROVED'
  | 'ESTIMATE_DECLINED'
  | 'JOB_COMPLETED'
  | 'SURVEY_LOW_SCORE'
  | 'BUNDLE_RENEWAL_PENDING'
  | 'BUNDLE_REQUEST_PENDING'
  | 'BUNDLE_RENEWAL_BLOCKED';

/** The records an alert can open (the page is chosen by staffNotificationPath). */
export type StaffNotificationLinkType =
  | 'SERVICE_REQUEST'
  | 'RESCHEDULE_REQUEST'
  | 'CONVERSATION'
  | 'INVOICE'
  | 'PAYMENT'
  | 'ESTIMATE'
  | 'JOB'
  | 'CUSTOMER'
  | 'ENROLLMENT';

export interface StaffNotificationLink {
  type: StaffNotificationLinkType | string;
  id: string;
}

export interface StaffNotification {
  id: string;
  /** A StaffNotificationType (kept a string: a newer server may send a type this build does not know) */
  type: string;
  title: string;
  body: string | null;
  link: StaffNotificationLink | null;
  readAt: string | null;
  createdAt: string;
}

export interface StaffNotificationListQuery {
  unread?: boolean;
  type?: StaffNotificationType;
  page?: number;
  /** At most 50 */
  limit?: number;
}

export interface StaffNotificationPage {
  data: StaffNotification[];
  total: number;
  page: number;
  limit: number;
  /** The caller's unread alerts, capped at 100 */
  unread: number;
}

export interface StaffNotificationUnreadCount {
  unread: number;
  /** The real number is at least the cap (100) */
  capped: boolean;
}

export interface StaffNotificationMarkAllInput {
  /** Only alerts created at or before this ISO time (so ones that arrived while the list was open stay unread) */
  before?: string;
  type?: StaffNotificationType;
}

export interface StaffNotificationPreference {
  type: StaffNotificationType | string;
  label: string;
  description: string;
  muted: boolean;
}

export interface StaffNotificationPreferences {
  types: StaffNotificationPreference[];
}

/** Backend error codes of the staff notification routes. */
export const STAFF_NOTIFICATION_ERROR_CODES = {
  unknownType: 'STAFF_NOTIFICATION_UNKNOWN_TYPE',
} as const;
